import React, { useCallback, useEffect, useState } from 'react';
import type { Direction } from '@prisjakt/ui';
import { ButtonType } from '@prisjakt/ui/button';
import {
  Dropdown,
  DropdownContent,
  DropdownToggle,
} from '@prisjakt/ui/dropdown';
import { RadioButton } from '@prisjakt/ui/radiobutton';
import { DropdownItem } from './styles';
import { IconSort } from '@prisjakt/ui/icon/sort';

export enum OptionTypeEnum {
  CHEAPEST_PRICE = 'CHEAPEST_PRICE',
  FASTEST_DELIVERY = 'FASTEST_DELIVERY',
}

export type Option = {
  title: string;
  type?: OptionTypeEnum;
  value?: string;
};

type Props = {
  alignment?: Direction;
  className?: string;
  defaultSelected?: Option;
  onFilterChange: (option: Option) => void;
  options: Option[];
  title?: string;
  width?: number;
  showAsRadioButtons?: boolean;
  showIcon?: boolean;
};

export const FilterDropdown = ({
  alignment,
  className,
  defaultSelected,
  onFilterChange,
  options,
  width,
  showAsRadioButtons = false,
  showIcon = true,
}: Props) => {
  const [selected, setSelected] = useState<Option | undefined>(
    defaultSelected || options[0]
  );

  useEffect(() => {
    setSelected(defaultSelected);
  }, [defaultSelected]);

  const handleOnClick = useCallback(
    (option: Option) => {
      setSelected(option);
      onFilterChange(option);
    },
    [onFilterChange]
  );

  return (
    <Dropdown
      closeOnItemClick
      className={className}
      containerProps={{ 'data-test': 'FilterDropdown' }}
    >
      <DropdownToggle
        showActiveIcon
        type={ButtonType.TEXT}
        containerProps={{ 'data-test': 'DropdownToggle' }}
      >
        {showIcon && <IconSort />}
        {selected?.title}
      </DropdownToggle>
      <DropdownContent alignment={alignment} width={width}>
        <Content
          onClick={handleOnClick}
          options={options}
          selected={selected}
          showAsRadioButtons={showAsRadioButtons}
        />
      </DropdownContent>
    </Dropdown>
  );
};

type ContentProps = {
  onClick: (option: Option) => void;
  options: Option[];
  selected?: Option;
  mobileOnly?: boolean;
  showAsRadioButtons?: boolean;
};

export const Content = (props: ContentProps) => {
  const { onClick, options, selected, showAsRadioButtons } = props;
  return (
    <>
      {options.sort().map(option => {
        return (
          <span key={option.title}>
            {showAsRadioButtons ? (
              <RadioButton
                text={option.title}
                selectedValue={selected?.value === option.value}
                value
                onClickHandler={() => onClick(option)}
              />
            ) : (
              <DropdownItem
                onClick={() => onClick(option)}
                disabled={Boolean(selected?.title === option.title)}
                data-test="DropdownOption"
              >
                {option.title}
              </DropdownItem>
            )}
          </span>
        );
      })}
    </>
  );
};
